import type {
  IClientSupervision,
  IParentSupervision,
  NarrativeSuggestionContext,
} from './types';

export type { NarrativeSuggestionContext, NarrativeSuggestionNoteType } from './types';

export function getNarrativeSuggestionContextKey({
  noteId,
  noteType,
  sourceKey,
}: NarrativeSuggestionContext): string {
  return JSON.stringify([noteType, noteId, sourceKey]);
}

export function getClientSupervisionNarrativeSourceKey(
  supervision: IClientSupervision | null,
): string {
  if (!supervision) return '';

  return JSON.stringify({
    date_start: supervision.date_start,
    utc_offset_date_start: supervision.utc_offset_date_start,
    duration_seconds: supervision.duration,
    location: supervision.location,
    setting: supervision.setting,
    clinical_status: supervision.clinical_status,
    response_to_treatment: supervision.response_to_treatment,
    overall_progress_toward_mastery: supervision.overall_progress_toward_mastery,
    evaluation_flags: {
      modified_behaviour_intervention: supervision.modified_behaviour_intervention,
      assessed_modification: supervision.assessed_modification,
      probed_new_skills: supervision.probed_new_skills,
      reviewed_and_analyzed_data: supervision.reviewed_and_analyzed_data,
      reviewed_and_evaluated_session_notes: supervision.reviewed_and_evaluated_session_notes,
      reviewed_and_evaluated_treatment_plan: supervision.reviewed_and_evaluated_treatment_plan,
      evaluated_progress: supervision.evaluated_progress,
      parent_demonstrated_new_or_changed_skills:
        supervision.parent_demonstrated_new_or_changed_skills,
    },
    target_phase_changes: (supervision.target_phase ?? [])
      .map((phase) => ({
        id: phase.id,
        target_id: phase.target_id,
        title: phase.title,
        date_added: phase.date_added,
      }))
      .sort((left, right) => {
        const dateOrder = String(left.date_added ?? '').localeCompare(
          String(right.date_added ?? ''),
        );
        return dateOrder || Number(left.id ?? 0) - Number(right.id ?? 0);
      }),
  });
}

export function getParentSupervisionNarrativeSourceKey(
  supervision: IParentSupervision | null,
): string {
  if (!supervision) return '';

  return JSON.stringify({
    date_start: supervision.date_start,
    utc_offset_date_start: supervision.utc_offset_date_start,
    duration_seconds: supervision.expected_duration,
    location: supervision.location,
    setting: supervision.setting,
    version: supervision.version,
    parent_progress: supervision.parent_progress,
    goals_and_targets_adhered: supervision.goals_and_targets_adhered,
    goal_progress: (supervision.goal_progress ?? [])
      .map((progress) => ({
        id: progress.id,
        goal_id: progress.goal_id,
        goal_evaluation: progress.goal_evaluation,
      }))
      .sort((left, right) => Number(left.id ?? 0) - Number(right.id ?? 0)),
    target_trials: supervision.target_progress ?? [],
  });
}

export function toParentSupervisionNoteId(
  value: number | string | null | undefined,
): number | null {
  if (value === null || value === undefined || value === '') return null;
  const noteId = Number(value);
  return Number.isFinite(noteId) ? noteId : null;
}

export function matchesActiveParentSupervisionNote(
  activeNoteId: number | null,
  noteId: number | string | null | undefined,
): boolean {
  const resolvedNoteId = toParentSupervisionNoteId(noteId);
  if (resolvedNoteId === null) return false;
  if (activeNoteId === null) return false;
  return resolvedNoteId === activeNoteId;
}

export function isCurrentParentSupervisionSave(
  current: IParentSupervision | null,
  savedNoteId: number,
  context?: NarrativeSuggestionContext,
): boolean {
  if (current?.id !== savedNoteId) return false;
  if (!context) return true;
  return (
    context.noteId === current.id &&
    getParentSupervisionNarrativeSourceKey(current) === context.sourceKey
  );
}
